import React from 'react';
import { useParams } from 'react-router-dom';
import { theme } from '../../../../../../common/styles/theme';
import { Box, Stack } from '@mui/material';
import { Paper } from '../../../../../../common/components/Paper/Paper';
import { IAudioProps } from './types';
import { AudioCaptionsContainerHOC } from '../../AudioCaptions/CaptionsContainer/hocs/AudioCaptionsContainerHOC';
import AudioPlayer, { RHAP_UI } from 'react-h5-audio-player';
// @ts-ignore
import css from '!!raw-loader!react-h5-audio-player/lib/styles.css';
import { createGlobalStyle } from 'styled-components';
import { PlayButton } from '../../../../PlayButton/components/PlayButton';
import { DeviceContext } from '../../../../../../common/contexts/DeviceContext';
import { CLASS_PLAY_CONTROL } from './constants';
import { Splitter } from '../../Splitter/Splitter';
import { useIsMobileLayout } from '../../../../../../common/hooks/useIsMobileLayout';

const PlayerStyle = createGlobalStyle`${css}` as any;

export const Audio: React.FC<IAudioProps> = ({
	groupsLoaded,
	audioUrl,
	videoUrl,
	showPlayer,
	onLoad,
	onUpdateAudioPlayer,
	onUpdateGroupId,
	onKeyDown,
	onKeyUp,
	onDocClick,
}) => {

	let { audioId, groupId } = useParams<{audioId: string, groupId: string}>();

	const elemRef = React.useRef<AudioPlayer>(null);
	const videoElemRef = React.useRef<HTMLVideoElement>(null);
	React.useEffect(() => {
		if (videoUrl) {
			if (videoElemRef.current) {
				onUpdateAudioPlayer(videoElemRef.current);
			}
		} else if (elemRef.current) {
			onUpdateAudioPlayer(elemRef.current.audio.current);
		}
	}, [audioUrl, videoUrl]);

	// Drag-to-resize for the video box via the Splitter below it: videoBoxRef is the box whose height
	// is dragged directly (isResized then skips the auto 16:9 height on later layout changes).
	const videoBoxRef = React.useRef<HTMLElement | null>(null);
	const isResized = React.useRef(false);

	const initVideoHeight = () => {
		const video = videoBoxRef.current as HTMLElement;
		if (video) {
			let newHeight = (video.offsetWidth / 16) * 9;
			const maxHeight = window.innerHeight / 3;
			if (newHeight > maxHeight) newHeight = maxHeight;
			video.style.height = newHeight + 'px';
		}
	};

	React.useEffect(() => {
		if (!videoUrl) return;
		if (!isResized.current) initVideoHeight();
	}, [videoUrl]);

	React.useEffect(() => {
		if (!videoUrl) return;

		const onWindowResize = () => {
			if (!isResized.current) initVideoHeight();
		};
		window.addEventListener('resize', onWindowResize);
		return () => {
			window.removeEventListener('resize', onWindowResize);
		};
	}, [videoUrl]);

	const onVideoSplitterDrag = (clientY: number) => {
		const video = videoBoxRef.current;
		if (!video) return;
		video.style.height = Math.max(0, clientY - video.getBoundingClientRect().top) + 'px';
		isResized.current = true;
	};

	// double-click goes back to the automatic 16:9 height
	const onVideoSplitterReset = () => {
		isResized.current = false;
		initVideoHeight();
	};

	React.useEffect(() => {
		if (groupsLoaded) {
			const gId = parseInt(groupId);
			const aId = parseInt(audioId);
			onLoad(aId, gId);
		}
	}, [groupId, audioId, groupsLoaded])

	React.useEffect(() => {
		onUpdateGroupId(parseInt(groupId));
	  }, [groupId]);

	React.useEffect(() => {

		const controller = new AbortController();
		const opt = { signal: controller.signal }
		document.addEventListener('keyup', (event: KeyboardEvent) => {
			onKeyUp(event);
		}, opt);
		document.addEventListener('keydown', (event: KeyboardEvent) => {
			onKeyDown(event);
		}, opt);
		document.addEventListener('click', (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			if (!target.closest(`.${CLASS_PLAY_CONTROL}`) && !target.closest('.captions-parent') ) {
				onDocClick();
			}
		}, opt);
		return () => {
			controller.abort();
		};
	}, [audioId, groupId]);

	const { isMobile } = React.useContext(DeviceContext);
	const isMobileLayout = useIsMobileLayout();

	return (
		<Stack
			className={'video-column popup-column popup-column--left phrases-column-1'}
			gap={1}
			justifyContent={'space-between'}
			sx={{
				position: 'relative',
				flex: `1 1 33%`,
				minWidth: 0,
				minHeight: 0,

				[`&.phrases-column-1`]: {
					flexBasis: '50%',
				},
			}}
		>
			{showPlayer && videoUrl && <>
			<Paper
				className={`audio-player ${CLASS_PLAY_CONTROL}`}
				sx={{
					position: 'relative',
					background: 'none',
				}}
			>
				<Box
					ref={videoBoxRef}
					sx={{
						position: 'relative',
						flex: '1 1 360px',
						minHeight: theme.spacing(20),
						maxHeight: `calc(100vh - ${theme.spacing(40)})`,

						'@media (max-height: 500px)': {
							minHeight: theme.spacing(13),
						},
					}}
				>
					<Box
						sx={{
							width: 1,
							height: 1,
							borderRadius: theme.spacing(0.75),
							overflow: 'hidden',
							background: theme.palette.common.black,
						}}
					>
						<video
							key={videoUrl}
							src={videoUrl}
							controls
							// iOS would otherwise go fullscreen on play (e.g. a timestamp tap in exercises)
							playsInline
							ref={videoElemRef}
							style={{ width: '100%', height: '100%', display: 'block' }}
						/>
					</Box>
				</Box>
			</Paper>
			{!isMobile && !isMobileLayout &&
				// negative my eats half the column gap on each side, so the handle doesn't add a big gap
				<Splitter
					orientation={'row'}
					onDrag={onVideoSplitterDrag}
					onDoubleClick={onVideoSplitterReset}
					sx={{ my: `${theme.spacing(-.5)} !important` }}
				/>
			}
			</>
			}

			{showPlayer && !videoUrl && audioUrl && <Paper
				className={`audio-player ${CLASS_PLAY_CONTROL}`}
				sx={{
					p: theme.spacing(1),

					'.rhap_container': {
						position: 'relative',
						p: 0,
						background: 'none',
						boxShadow: 'none',
					},

					'.rhap_time': {
						fontFamily: 'monospace',
						fontSize: theme.typography.fontSize,
						color: theme.palette.grey[500],
					},

					'.rhap_progress-section': {
						height: theme.spacing(4),
						pl: theme.spacing(5),
					},

					'.rhap_stacked .rhap_controls-section': {
						m: 0,
						position: 'absolute',
						left: 0,
						top: 0,
					},

					'.rhap_main-controls': {
						width: theme.spacing(4),
						height: theme.spacing(4),
					},

					'.rhap_main-controls-button': {
						m: 0,
						color: theme.palette.primary.main,
						outline: 'none',
					},

					'.rhap_progress-indicator': {
						background: theme.palette.primary.main,
						boxShadow: 'none',
						transform: 'scale(0)',
						transition: 'transform .3s ease',
					},

					'.rhap_progress-bar, .rhap_download-progress': {
						background: theme.palette.primary[100],
					},

					'.rhap_progress-container': {
						m: theme.spacing(0, 2),
						outline: 'none',
						'&:hover': {
							'.rhap_progress-indicator': {
								transform: 'scale(1)',
							},
						},
					},

					'.rhap_progress-filled': {
						background: theme.palette.primary.main,
					},
				}}
			>
				{audioUrl &&
				<AudioPlayer
					src={audioUrl}
					showJumpControls={false}
					hasDefaultKeyBindings={false}
					customProgressBarSection={[
						RHAP_UI.CURRENT_TIME,
						RHAP_UI.PROGRESS_BAR,
						RHAP_UI.CURRENT_LEFT_TIME,
					]}
					customControlsSection={[
						RHAP_UI.MAIN_CONTROLS,
					]}
					customIcons={{
						play: <PlayButton
							isPlay={false}
							onClick={() => {
							}}
						/>,
						pause: <PlayButton
							isPlay={true}
							onClick={() => {
							}}
						/>,
					}}
					ref={elemRef}
				/>
				}
			</Paper>
			}

			<Stack
				sx={{
					flex: '1 1 100%',
					minHeight: 0,
				}}
			>
				<AudioCaptionsContainerHOC />
			</Stack>

			<PlayerStyle />
		</Stack>
	);
};
